'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers-extra'

const MagnitudeLadder = dynamic(() => import('../_components/widgets/FoundationWidgets').then(m => m.MagnitudeLadder), { ssr: false })
const NumberFormats = dynamic(() => import('../_components/widgets/FoundationWidgets').then(m => m.NumberFormats), { ssr: false })

export default function BigAndSmallNumbers() {
  return (
    <Lesson>
      <Step>
        <H>From tiny to gigantic</H>
        <P>
          AI deals with numbers at both extremes at once. A learning rate might be 0.0003, while a model has 175 000 000 000
          parameters. Writing all those zeros is slow and easy to get wrong, so we use <strong>scientific notation</strong>: a
          number between 1 and 10, times a power of 10.
        </P>
        <MB>{'175\\,000\\,000\\,000 = 1.75 \\times 10^{11} \\qquad 0.0003 = 3 \\times 10^{-4}'}</MB>
        <P>
          The power says how many places to move the decimal point: right for positive powers, left for negative ones. In code
          it’s written with an <C>e</C>: <C>1.75e11</C> and <C>3e-4</C>.
        </P>
        <Figure n="2.10" wide caption="Real numbers from AI, from 0.00000001 to 15 trillion. On an ordinary axis they’re invisible; on a log axis every factor of ten gets the same room.">
          <MagnitudeLadder />
        </Figure>
      </Step>

      <Step gate={['nq-sci', 'q-log-axis']}>
        <H>Reading powers of ten</H>
        <NumberQuiz
          id="nq-sci"
          question={
            <>
              A model has <M>{'7 \\times 10^{9}'}</M> parameters. Another has <M>{'7 \\times 10^{11}'}</M>. How many times bigger
              is the second?
            </>
          }
          answer={100}
          explain={
            <>
              The powers differ by 2, so it’s <M>{'10^2 = 100'}</M> times bigger. Multiplying powers of ten means adding their
              exponents — that’s the logarithm rule from last lesson in action.
            </>
          }
          hint={<>Compare the powers: 11 − 9. Each step of the power is another ×10.</>}
        />
        <Quiz
          id="q-log-axis"
          question="On a log axis, the gap from 1 to 10 is 3 cm. How long is the gap from 1 000 to 10 000?"
          options={[
            { label: '3 cm', correct: true },
            { label: '3 000 cm', why: 'That’s what an ordinary axis would need. A log axis gives every ×10 the same space.' },
            { label: '9 cm', why: 'Each step is the same ×10, so each gets the same length.' },
          ]}
          explain="That’s why training curves and scaling laws are drawn on log axes: you can see 10 steps and 10 million steps on the same chart."
        />
      </Step>

      <Step>
        <H>Computers round everything</H>
        <P>
          A computer stores a number in a fixed amount of memory, measured in <strong>bits</strong>. JavaScript uses 64 bits per
          number, enough for about 16 significant digits. Anything past that is rounded. That’s why <C>0.1 + 0.2</C> isn’t
          exactly <C>0.3</C>.
        </P>
        <Exercise id="floats" title="Where numbers break" {...EX.floats} />
        <Callout kind="warn">
          Never test decimals with <C>===</C>. Ask whether they’re <em>close enough</em> instead:{' '}
          <C>{'Math.abs(a - b) < 1e-9'}</C>.
        </Callout>
      </Step>

      <Step gate="q-bits">
        <H>Fewer bits, faster AI</H>
        <P>
          A 70-billion-parameter model at 32 bits per number needs 280 GB of memory. At 16 bits it needs half that. So modern AI
          stores its numbers in fewer bits and accepts less precision. Compare how the same number survives in each format:
        </P>
        <Figure n="2.11" wide caption="One number, four storage formats. Fewer bits means bigger rounding errors, and a smaller range before numbers turn into Infinity or 0.">
          <NumberFormats />
        </Figure>
        <Quiz
          id="q-bits"
          question="Why do people train models in bfloat16 rather than float16, when both use 16 bits?"
          options={[
            {
              label: 'bfloat16 keeps float32’s huge range (it rarely overflows), at the cost of fewer digits',
              correct: true,
            },
            { label: 'bfloat16 is more precise', why: 'It’s actually less precise — it keeps fewer digits. Try π in the table.' },
            { label: 'float16 can’t store negative numbers', why: 'Both store negative numbers fine.' },
          ]}
          explain="Gradients can be very small or very large. Running out of range breaks training outright; a little rounding usually doesn’t."
        />
      </Step>

      <Step gate="sci-notation">
        <H>Tools for careful numbers</H>
        <Exercise
          id="sci-notation"
          title="approxEqual and toSci"
          {...EX.sciNotation}
          hints={[
            <>
              <C>{'return Math.abs(a - b) <= tol'}</C>
            </>,
            <>
              <C>const e = Math.floor(Math.log10(x))</C>, then the mantissa is <C>{'x / 10 ** e'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="stable-math">
        <H>Two tricks that keep AI from breaking</H>
        <P>
          The exponential function from last lesson grows so fast that <M>{'e^{710}'}</M> is already too big to store. And
          multiplying hundreds of small probabilities together quickly gives a number too small to store, so it becomes 0. Both
          happen all the time in AI. Both have neat fixes:
        </P>
        <Exercise
          id="stable-math"
          title="Dodge overflow and underflow"
          {...EX.stableMath}
          hints={[
            <>
              <C>{'return 1 / (1 + Math.exp(b - a))'}</C> — the exponent is now small whenever a is the bigger one.
            </>,
            <>
              <C>{'for (const p of ps) total += Math.log(p)'}</C>
            </>,
          ]}
        />
        <Callout kind="why">
          You’ll see both again. The first trick is inside every <strong>softmax</strong> (Module 5). The second is why models
          are trained on <em>log</em>-probabilities: the log of a product of a thousand probabilities is a perfectly ordinary
          number.
        </Callout>
      </Step>
    </Lesson>
  )
}
